import React, { useMemo } from 'react';
import { defaultTo, isEmpty, isNil, prop, uniqBy, join, map, compose, sortBy } from 'ramda';
import VariablesBlock from './VariablesBlock';

// `documentVariables` is only supplied by EditWidgetModal / CreateWidgetModal, i.e.
// when the widget editor is opened from within a document. Everywhere else it stays
// undefined, which is what hides the document block (FLEXIDOCS-2917).
const VariablesLibrary = ({ documentVariables, predefinedVariables = [] }) => {
  const isInDocumentContext = !isNil(documentVariables);
  const hasPredefined = !isEmpty(predefinedVariables);

  // Memoized so the new array identity does not defeat the useMemo inside
  // useVariablesSearch on every render.
  const uniqueDocumentVariables = useMemo(
    () => compose(
      sortBy(prop('key')),
      uniqBy(prop('key')),
      defaultTo([])
    )(documentVariables),
    [documentVariables]
  );

  // Duplicate keys coming from the backend would produce duplicate React keys.
  const uniquePredefinedVariables = useMemo(
    () => uniqBy(prop('key'), predefinedVariables),
    [predefinedVariables]
  );
  
  const infoPredefinedVariables = useMemo(() => (
    <p className="p-2 whitespace-pre-line">Des variables prédéfinies sont
      disponibles
      : <br/>{join(',\r\n', map(pred => `- ${prop('key', pred)}: ${prop('description', pred)}`, predefinedVariables))}
    </p>
  ), [predefinedVariables]);

  if (!isInDocumentContext && !hasPredefined) return null;

  // An in-flow column, mirroring the 210px #style-bar on the right, so that the canvas
  // centres between two symmetric columns (an overlay cannot: the canvas box would
  // still start underneath it). Accepted trade-off, no breakpoint: the canvas has a
  // fixed pixel width (794px portrait, 1123px landscape), so below ~1400px of viewport
  // it gets clipped without a scrollbar.
  // No scroll container here on purpose: each block caps its own list, which bounds the
  // whole column. A second, outer scroller would stack two scrollbars on top of each
  // other for no benefit.
  return (
    <div className="w-210px shrink-0 space-y-2" id="variables-library-bar">
      {isInDocumentContext && (
        <VariablesBlock
          title="ajouter variable"
          variables={uniqueDocumentVariables}
          searchable
          emptyLabel="Aucune variable dans ce document."
        />
      )}
      {hasPredefined && (
        <VariablesBlock
          title="variables prédéfinies"
          variables={uniquePredefinedVariables}
          emptyLabel="Aucune variable prédéfinie."
          info={infoPredefinedVariables}
        />
      )}
    </div>
  );
};

export default VariablesLibrary;
